"use client";

import React from "react";
import { useSearchParams } from "react-router-dom";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader, Cpu, Wrench, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/10 border border-primary/20 rounded-lg flex flex-col items-center justify-center text-center h-full min-h-[400px]">
                <div className="size-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
                    <Cpu className="size-6 text-primary" />
                </div>
                <h3 className="font-default text-xl font-semibold text-foreground mb-2">Inquiry Received</h3>
                <p className="text-muted-foreground mb-6 max-w-md">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary/50 hover:bg-primary/10">
                    Submit Another Inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters required"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Full name</FormLabel>
                            <FormControl>
                                <Input placeholder="John Doe" className="rounded-lg border-foreground/20 focus-visible:border-primary bg-background" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Contact Email</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="john@company.com" className="rounded-lg border-foreground/20 focus-visible:border-primary bg-background" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Contact Phone</FormLabel>
                        <FormControl>
                            <Input type="tel" placeholder="+27 82 553 8657" className="rounded-lg border-foreground/20 focus-visible:border-primary bg-background" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="partNumber" rules={{
      required: "Part number is required for accurate support"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Siemens Part Number / Model</FormLabel>
                        <FormControl>
                            <Input placeholder="e.g., 6FC5370-0AA00-3AA0" className="rounded-lg border-foreground/20 focus-visible:border-primary bg-background font-mono" {...field} />
                        </FormControl>
                        <FormDescription className="font-mono text-[10px]">Required for precise component matching and availability checks.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="message" rules={{
      required: "Inquiry details are required",
      minLength: {
        value: 10,
        message: "Please provide more details"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Technical Specifications / Issue Description</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Describe the required part, current machine state, or specific technical requirements..." className="min-h-[150px] rounded-lg border-foreground/20 focus-visible:border-primary bg-background resize-y" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <p className="text-destructive text-sm font-mono">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-mono uppercase tracking-wider px-8">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-4" />
                        Processing...
                    </> : <>
                        Submit inquiry
                        <ArrowRight className="ml-2 size-4" />
                    </>}
            </Button>
        </Form>;
}
export default function ProductInquiry() {
  const [searchParams] = useSearchParams();
  const initialPartNumber = searchParams.get("sku") || searchParams.get("product") || "";
  return <section id="productinquiry" className="bg-background py-16 md:py-24 relative overflow-hidden">
            
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    
                    
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="mb-8">
                            <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground mb-4">Parts Inquiry</h2>
                            <p className="text-muted-foreground text-lg">
                                Submit your part requirements for immediate processing and availability verification.
                            </p>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-md">
                            <FormProvider formId="siemens-part-inquiry" sectionName="ProductInquiry" defaultValues={{
              name: "",
              email: "",
              phone: "",
              partNumber: initialPartNumber,
              message: ""
            }} submitText="Submit inquiry" successMessage="Inquiry transmitted successfully. Our technical team will review the specifications and contact you shortly.">
                                <InquiryForm />
                            </FormProvider>
                        </div>
                    </div>

                    
                    <div className="lg:col-span-5 order-1 lg:order-2 flex flex-col">
                        <div className="relative pl-6 md:pl-8 border-l-2 border-primary h-full flex flex-col">
                            
                            
                            <h3 className="font-default text-2xl font-semibold text-foreground mb-4">
                                Precision Siemens Solutions
                            </h3>
                            
                            <p className="text-muted-foreground mb-8 leading-relaxed">
                                IMCNC Designs specializes in the procurement, diagnostics, and integration of high-fidelity Siemens CNC components. Our direct access to specialized inventories ensures rapid deployment of critical spare parts to minimize operational downtime.
                            </p>

                            <div className="space-y-6 mb-8 flex-grow">
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-secondary/10 rounded-lg shrink-0 mt-1">
                                        <Cpu className="size-5 text-secondary" />
                                    </div>
                                    <div>
                                        <h4 className="font-default font-medium text-foreground">Sinumerik Systems</h4>
                                        <p className="text-sm text-muted-foreground mt-1">Comprehensive support for Sinumerik One, 840D sl, and legacy Powerline architectures.</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-secondary/10 rounded-lg shrink-0 mt-1">
                                        <Wrench className="size-5 text-secondary" />
                                    </div>
                                    <div>
                                        <h4 className="font-default font-medium text-foreground">Drive Technologies</h4>
                                        <p className="text-sm text-muted-foreground mt-1">Sinamics S120 modules, Simodrive 611 components, and associated motor encoders.</p>
                                    </div>
                                </div>
                            </div>

                            <Separator className="my-8" />

                            <div className="space-y-4">
                                <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4">Direct Communication Channels</h4>
                                
                                <Link to="mailto:charles@imcncdesigns.co.za" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors group">
                                    <Mail className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                    <span className="font-mono text-sm">charles@imcncdesigns.co.za</span>
                                </Link>
                                
                                <Link to="tel:0825538657" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors group">
                                    <Phone className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                    <span className="font-mono text-sm">+27 82 553 8657</span>
                                </Link>
                                
                                <div className="flex items-start gap-3 text-foreground">
                                    <MapPin className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="font-mono text-sm leading-tight">
                                        Wilge rd., Golfpark<br />
                                        Gauteng, South Africa, 1961
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}